import { produce } from 'immer';
import { noop } from 'lodash';
import { getType } from 'typesafe-actions';

import { LocalStorage, StorageKeyType } from '../../service/Storage/Storage';
import { findItem } from '../common/helpers';

import {
    applyFilter,
    applyFilterSuccess,
    closeCampaignsList,
    closePreview,
    enableEditMode,
    getCampaigns,
    getCampaignsSuccess,
    getPreview,
    hideError,
    loadItems,
    loadItemsSuccess,
    nameChange,
    openPreview,
    resetNameToInitial,
    saveName,
    saveNameSuccess,
    setSelection,
    showError,
    toggleItemSelection,
    togglePlaySound,
    VideoCreativesAction
} from './actions';
import { VideoCreativesState } from './typings';

export const getVideoCreativesInitialState = (isAudio: boolean = false): VideoCreativesState => {
    return {
        collection: {
            items: [],
            total: 0,
            offset: 0
        },
        campaigns: [],
        campaignsListVisible: false,
        campaignsLoading: false,
        error: undefined,
        filterText: '',
        showTiedSelected: true,
        playSoundSelected: LocalStorage.getItem(StorageKeyType.isVideoCreativesSoundEnabled) as boolean,
        presetsUrl: '',
        className: '',
        isBusy: false,
        onCancel: (): void => noop(),
        previewVisible: false,
        previewVast: '',
        previewId: '',
        isAudio
    };
};

function changeSelection(id: string, selected: boolean, draft: VideoCreativesState): void {
    const { collection } = draft;
    const foundItem = findItem(id, collection.items);

    collection.items.forEach(item => {
        if (foundItem.id === item.id) {
            item.selected = selected;
            item.shadowed = false;
        } else {
            item.selected = false;
            item.shadowed = selected;
        }
    });

    if (selected) {
        draft.selectedCreativeId = foundItem.creativeId;
    } else {
        draft.selectedCreativeId = undefined;
    }
}

export function videoCreatives(
    state: VideoCreativesState = getVideoCreativesInitialState(),
    action: VideoCreativesAction
): VideoCreativesState {
    return produce(state, draft => {
        switch (action.type) {
            case getType(applyFilter): {
                draft.filterText = action.text;
                draft.showTiedSelected = action.showTied;
                draft.isBusy = true;

                return;
            }

            case getType(loadItems): {
                draft.isBusy = true;

                return;
            }

            case getType(getPreview): {
                draft.previewId = action.id;

                return;
            }

            case getType(openPreview): {
                draft.previewVisible = true;
                draft.previewVast = action.vast;

                return;
            }

            case getType(closePreview): {
                draft.previewVisible = false;
                draft.previewVast = '';
                draft.previewId = '';

                return;
            }

            case getType(enableEditMode): {
                const item = findItem(action.id, draft.collection.items);

                item.editMode = action.enabled;

                return;
            }

            case getType(togglePlaySound): {
                draft.playSoundSelected = !state.playSoundSelected;
                LocalStorage.setItem(StorageKeyType.isVideoCreativesSoundEnabled, draft.playSoundSelected);

                return;
            }

            case getType(toggleItemSelection): {
                const item = findItem(action.id, draft.collection.items);

                changeSelection(action.id, !item.selected, draft);

                return;
            }

            case getType(setSelection): {
                changeSelection(action.id, true, draft);
                return;
            }

            case getType(nameChange): {
                const item = findItem(action.id, draft.collection.items);

                item.nameInputValue = action.val;

                return;
            }

            case getType(resetNameToInitial): {
                const item = findItem(action.id, draft.collection.items);

                item.nameInputValue = item.name;
                draft.isBusy = false;

                return;
            }

            case getType(saveName): {
                draft.isBusy = true;

                return;
            }

            case getType(saveNameSuccess): {
                const { collection } = draft;
                const item = findItem(action.id, collection.items);

                if (item.nameInputValue) {
                    item.name = item.nameInputValue;
                } else {
                    item.nameInputValue = item.name;
                }
                draft.isBusy = false;

                return;
            }

            case getType(loadItemsSuccess): {
                const { collection } = draft;

                if (state.selectedCreativeId !== undefined) {
                    action.collection.items.forEach(item => (item.shadowed = true));
                }

                collection.items = collection.items.concat(action.collection.items);
                collection.offset = action.collection.offset;
                draft.isBusy = false;

                return;
            }

            case getType(applyFilterSuccess): {
                draft.collection = action.collection;
                draft.isBusy = false;

                if (state.selectedCreativeId !== undefined) {
                    draft.collection.items.forEach(item => {
                        if (item.creativeId === state.selectedCreativeId) {
                            item.selected = true;
                        } else {
                            item.shadowed = true;
                        }
                    });
                }

                return;
            }

            case getType(getCampaignsSuccess): {
                draft.campaigns = action.items;
                draft.campaignsLoading = false;

                return;
            }

            case getType(showError): {
                draft.isBusy = false;
                draft.campaigns = [];
                draft.campaignsListVisible = false;
                draft.campaignsLoading = false;
                draft.error = { message: action.message };

                return;
            }

            case getType(hideError): {
                draft.error = undefined;

                return;
            }

            case getType(getCampaigns): {
                draft.campaignsListVisible = true;
                draft.campaignsLoading = true;

                return;
            }

            case getType(closeCampaignsList): {
                draft.campaigns = [];
                draft.campaignsListVisible = false;

                return;
            }

            default:
                return;
        }
    });
}
